import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../services/ApiService";

/* ── Logo ──────────────────────────────────────────────────── */
const Logo = ({ size = 38 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 140 140" fill="none">
    <defs>
      <linearGradient id="cs-ng" x1="30" y1="15" x2="110" y2="125">
        <stop offset="0%" stopColor="#00FFE0" /><stop offset="50%" stopColor="#00E5CC" /><stop offset="100%" stopColor="#22D97F" />
      </linearGradient>
    </defs>
    <rect width="140" height="140" rx="30" fill="#0F2140" />
    <path d="M70 18L28 37v32c0 26 42 45 42 45s42-19 42-45V37L70 18z" fill="none" stroke="url(#cs-ng)" strokeWidth="4" />
    <circle cx="70" cy="60" r="16" fill="none" stroke="url(#cs-ng)" strokeWidth="2.5" opacity="0.35" />
    <text x="70" y="68" textAnchor="middle" fontFamily="Sora,sans-serif" fontWeight="800" fontSize="20" fill="url(#cs-ng)">J</text>
  </svg>
);

const Ico = {
  arrowLeft: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></svg>,
  arrowRight: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14" /><path d="M12 5l7 7-7 7" /></svg>,
};

interface CaseStudy {
  _id: string;
  slug: string;
  title: string;
  coverImage?: string;
  excerpt?: string;
  client?: string;
  industry?: string;
}

export default function CaseStudyPublicList() {
  const navigate = useNavigate();
  const [caseStudies, setCaseStudies] = useState<CaseStudy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getMediaUrl = (url?: string | null): string => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const base = process.env.REACT_APP_MEDIA_BASE || "";
    if (url.startsWith("/uploads/")) return `${base}${url}`;
    return `${base}/uploads/case-studies/${url}`;
  };

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const res = (await api.get("/case-studies")) as any;
        const list = res.caseStudies ?? res.data?.caseStudies ?? res.data?.data ?? (Array.isArray(res.data) ? res.data : []);
        setCaseStudies(list);
      } catch (err) {
        setError("Failed to load case studies");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  return (
    <div className="csl-root">
      <style dangerouslySetInnerHTML={{ __html: CSL_STYLES }} />

      {/* NAV */}
      <nav className="csl-nav">
        <a href="#" className="csl-nav-logo" onClick={e => { e.preventDefault(); navigate('/'); }}>
          <Logo size={36} /><span className="csl-wm"><span className="jep">Jep</span><span className="tags">tags</span></span>
        </a>
        <button className="csl-back" onClick={() => navigate(-1)}>
          <span className="csl-ico-16">{Ico.arrowLeft}</span> Back
        </button>
      </nav>

      {/* HERO */}
      <section className="csl-hero">
        <div className="csl-badge">Success Stories</div>
        <h1>Case <span className="grad">Studies</span></h1>
        <p>Discover how our clients have achieved success with JepTags product authentication solutions.</p>
      </section>

      {/* LOADING */}
      {loading && (
        <div className="csl-grid-section">
          <div className="csl-grid">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="csl-skeleton">
                <div className="csl-sk-img" />
                <div className="csl-sk-body"><div className="csl-sk-line w75" /><div className="csl-sk-line w50" /></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ERROR */}
      {error && <div className="csl-msg error">{error}</div>}

      {/* EMPTY */}
      {!loading && !error && caseStudies.length === 0 && (
        <div className="csl-msg">No case studies available yet. Check back soon!</div>
      )}

      {/* GRID */}
      {!loading && !error && caseStudies.length > 0 && (
        <section className="csl-grid-section">
          <div className="csl-grid">
            {caseStudies.map(cs => (
              <Link key={cs._id} to={`/case-studies/${cs.slug}`} className="csl-card">
                <div className="csl-card-img">
                  {cs.coverImage ? (
                    <img src={getMediaUrl(cs.coverImage)} className="csl-media" alt={cs.title} />
                  ) : (
                    <div className="csl-placeholder"><span>📊</span></div>
                  )}
                </div>
                <div className="csl-card-body">
                  <h2>{cs.title}</h2>
                  {cs.client && <span className="csl-client">Client: {cs.client}</span>}
                  {cs.industry && <span className="csl-industry">Industry: {cs.industry}</span>}
                  {cs.excerpt && <p>{cs.excerpt}</p>}
                  <span className="csl-read-more">Read Case Study <span className="csl-ico-14">{Ico.arrowRight}</span></span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

const CSL_STYLES = `
.csl-root *,.csl-root *::before,.csl-root *::after{margin:0;padding:0;box-sizing:border-box}
.csl-root{
  --cyan:#00E5CC;--cyan-bright:#00FFE0;--teal:#22D97F;
  --navy-deep:#030a14;--navy:#0a1628;--navy-mid:#0F2140;
  font-family:'Sora',sans-serif;background:var(--navy-deep);color:#e0f0f0;overflow-x:hidden;min-height:100vh;
}
.csl-root a{text-decoration:none;color:inherit}
.grad{background:linear-gradient(135deg,var(--cyan-bright),var(--cyan),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.jep{color:var(--cyan)}.tags{color:var(--teal)}
.csl-ico-16 svg{width:16px;height:16px}.csl-ico-14 svg{width:14px;height:14px}

.csl-nav{position:fixed;top:0;left:0;right:0;z-index:100;padding:16px 40px;display:flex;align-items:center;justify-content:space-between;backdrop-filter:blur(20px);background:rgba(3,10,20,0.85);border-bottom:1px solid rgba(0,229,204,0.06)}
.csl-nav-logo{display:flex;align-items:center;gap:12px;text-decoration:none;cursor:pointer}
.csl-wm{font-size:22px;font-weight:700;letter-spacing:-.5px}
.csl-back{display:flex;align-items:center;gap:6px;background:none;border:1px solid rgba(0,229,204,0.15);color:rgba(224,240,240,0.6);font-family:'Sora',sans-serif;font-size:13px;padding:8px 16px;border-radius:8px;cursor:pointer;transition:all .3s}
.csl-back:hover{border-color:var(--cyan);color:var(--cyan)}

.csl-hero{padding:140px 40px 48px;text-align:center;max-width:720px;margin:0 auto}
.csl-badge{display:inline-block;padding:8px 20px;border-radius:100px;background:rgba(0,229,204,0.08);border:1px solid rgba(0,229,204,0.15);font-size:12px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:var(--cyan);margin-bottom:24px}
.csl-hero h1{font-size:clamp(32px,5vw,52px);font-weight:800;line-height:1.1;letter-spacing:-2px;margin-bottom:16px}
.csl-hero p{font-size:17px;line-height:1.7;color:rgba(224,240,240,0.5);max-width:560px;margin:0 auto}

.csl-msg{text-align:center;padding:60px 24px;color:rgba(224,240,240,0.35);font-size:16px}
.csl-msg.error{color:#f87171}

.csl-grid-section{max-width:1100px;margin:0 auto;padding:48px 24px 100px}
.csl-grid{display:grid;grid-template-columns:1fr 1fr;gap:24px}

.csl-card{border-radius:20px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06);overflow:hidden;transition:all .4s;display:flex;flex-direction:column}
.csl-card:hover{border-color:rgba(0,229,204,0.15);transform:translateY(-4px);box-shadow:0 20px 60px rgba(0,0,0,0.3)}
.csl-card-img{height:220px;overflow:hidden}
.csl-media{width:100%;height:100%;object-fit:cover;transition:transform .5s}
.csl-card:hover .csl-media{transform:scale(1.05)}
.csl-placeholder{height:100%;background:rgba(0,229,204,0.04);display:flex;align-items:center;justify-content:center;font-size:40px}
.csl-card-body{padding:24px;display:flex;flex-direction:column;gap:8px;flex:1}
.csl-card-body h2{font-size:20px;font-weight:700;line-height:1.3}
.csl-client{font-size:13px;font-weight:600;color:var(--cyan)}
.csl-industry{font-size:11px;font-weight:600;letter-spacing:.5px;text-transform:uppercase;color:rgba(224,240,240,0.3)}
.csl-card-body p{font-size:13px;line-height:1.6;color:rgba(224,240,240,0.4);display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
.csl-read-more{display:inline-flex;align-items:center;gap:6px;font-size:13px;font-weight:600;color:var(--cyan);margin-top:auto;padding-top:8px;transition:gap .3s}
.csl-card:hover .csl-read-more{gap:10px}

/* SKELETON */
.csl-skeleton{border-radius:20px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06);overflow:hidden}
.csl-sk-img{height:220px;background:rgba(0,229,204,0.03);animation:cslPulse 1.5s ease infinite}
.csl-sk-body{padding:24px}
.csl-sk-line{height:16px;border-radius:8px;background:rgba(0,229,204,0.05);margin-bottom:12px;animation:cslPulse 1.5s ease infinite}
.csl-sk-line.w75{width:75%}.csl-sk-line.w50{width:50%}
@keyframes cslPulse{0%,100%{opacity:.4}50%{opacity:.8}}

@media(max-width:900px){.csl-nav{padding:14px 20px}.csl-hero{padding:120px 24px 36px}}
@media(max-width:600px){.csl-grid{grid-template-columns:1fr}}
`;
